import { useEffect, useRef } from 'react';
import {
  ExclamationTriangleIcon,
  PauseCircleIcon,
} from '@heroicons/react/24/outline';
import { Button, Modal } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import { useSubscriptionManagerContext } from '../../../../SubscriptionManager.context';
import { useCancelSubscriptionContext } from '../CancelSubscription.context';

export type tPauseFinalizeConfirmationProps = {
  isOpen: boolean;
  /** Back to the pause offer. The customer has not cancelled anything yet. */
  onBack: () => void;
  /** Submits the real finalization; the caller owns what that does. */
  onConfirm: (event: React.FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
};

/**
 * The stop between declining the pause and actually finalizing.
 *
 * Declining was a one-click irreversible step sitting next to "Programar pausa",
 * on a screen whose whole subject is keeping the workspace intact — so the two
 * outcomes were a mis-click apart. This asks once, names what is lost, and offers
 * the pause again rather than only a way forward.
 *
 * Every loss it names is one that actually happens: access ends on the date the
 * subscription already carries, and the counts are this workspace's own, fetched at
 * the opening step. Nothing here claims data is deleted, because nothing deletes it.
 * A specific, true loss is also the stronger one — "tus 1,448 leads" lands harder
 * than a generic warning, and it survives a customer checking whether it was true.
 *
 * The three events exist so the gate can be judged on numbers: `dismissed` over
 * `viewed` is the share of people who came back, which is the whole question.
 */
export default function PauseFinalizeConfirmation(
  props: tPauseFinalizeConfirmationProps,
) {
  const { isOpen, onBack, onConfirm, isLoading } = props;
  const { t, i18n } = useTranslation('settings/SubscriptionManager');
  const { trackEvent } = useAnalytics();
  const { generalCancellationProperties } = useSubscriptionManagerContext();
  const { subscription } = useSubscriptionContext();
  const { totalLeadsCount, activeFunnelsCount, isLoadingLeads } =
    useCancelSubscriptionContext();

  const hasTrackedView = useRef(false);
  const properties = {
    ...generalCancellationProperties,
    leads_count: totalLeadsCount,
    funnels_count: activeFunnelsCount,
  };

  useEffect(() => {
    // The payload stamps counts, so it waits for the fetch that owns them to settle
    // rather than recording a workspace that merely looks empty.
    if (isLoadingLeads || hasTrackedView.current) return;
    hasTrackedView.current = true;
    trackEvent(
      'front.settings.retention_flow_s3_finalize_gate_viewed',
      properties,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingLeads]);

  const accessUntil = subscription.endDate
    ? new Intl.DateTimeFormat(i18n.language, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(new Date(subscription.endDate))
    : null;

  const hasCounts = !isLoadingLeads && totalLeadsCount > 0;

  const handleBack = () => {
    trackEvent(
      'front.settings.retention_flow_s3_finalize_gate_dismissed',
      properties,
    );
    onBack();
  };

  const handleConfirm = (event: React.FormEvent<HTMLFormElement>) => {
    trackEvent(
      'front.settings.retention_flow_s3_finalize_gate_confirmed',
      properties,
    );
    onConfirm(event);
  };

  return (
    <Modal
      open={isOpen}
      className="w-11/12 max-w-[560px] rounded-lg p-0"
      role="alertdialog"
      aria-labelledby="pause-finalize-confirm-title"
      aria-describedby="pause-finalize-confirm-body"
    >
      <Modal.Body className="flex flex-col items-center gap-4 px-8 py-8 text-center font-sans">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-warning/20">
          <ExclamationTriangleIcon
            aria-hidden="true"
            className="h-7 w-7 text-warning-content"
          />
        </span>

        <h2
          id="pause-finalize-confirm-title"
          className="text-2xl font-bold leading-tight text-neutral-800"
        >
          {t('cancelSubscription.pauseOffer.finalizeConfirm.heading')}
        </h2>

        <div
          id="pause-finalize-confirm-body"
          className="flex flex-col gap-3 text-base text-neutral-700"
        >
          <p data-testid="pause-finalize-loss-warning">
            {hasCounts
              ? t('cancelSubscription.pauseOffer.finalizeConfirm.lossCounted', {
                  leads: totalLeadsCount,
                  funnels: activeFunnelsCount,
                })
              : t('cancelSubscription.pauseOffer.finalizeConfirm.loss')}
          </p>
          {accessUntil ? (
            <p className="text-sm text-neutral-600">
              {t('cancelSubscription.pauseOffer.finalizeConfirm.accessUntil', {
                date: accessUntil,
              })}
            </p>
          ) : null}
        </div>

        <p className="flex items-start gap-2 rounded-lg bg-base-200 px-4 py-3 text-left text-sm text-neutral-700">
          <PauseCircleIcon
            aria-hidden="true"
            className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary"
          />
          {t('cancelSubscription.pauseOffer.finalizeConfirm.pauseHint')}
        </p>
      </Modal.Body>

      <Modal.Actions className="m-0 flex flex-col-reverse gap-3 border-t border-neutral-200 px-8 py-5 sm:flex-row sm:justify-end">
        {/* The way out is the primary action: this screen exists to make the
            irreversible one deliberate, not to hurry it along. */}
        <form onSubmit={handleConfirm} className="sm:mr-auto">
          <Button
            type="submit"
            color="ghost"
            size="lg"
            className="font-normal text-neutral-700"
            loading={isLoading}
            disabled={isLoading}
            data-testid="pause-finalize-confirm-button"
          >
            {t('cancelSubscription.pauseOffer.finalizeConfirm.confirmButton')}
          </Button>
        </form>
        <Button
          type="button"
          color="primary"
          size="lg"
          className="min-w-[200px] rounded-lg"
          onClick={handleBack}
          disabled={isLoading}
          data-testid="pause-finalize-back-button"
        >
          {t('cancelSubscription.pauseOffer.finalizeConfirm.backButton')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
